import { useEffect } from 'react'

import { toggleArray } from '@domain-group/javascript-utils'

import {
  Box,
  ListItem,
  SectionDivider,
  Text,
  ToggleButton,
} from '@domain-group/suit-theme'

import handleCallbackAction from '@functions/handleCallbackAction'

import type { AgencyModel } from '@models/types'

type UpdateWatermarkSettingParams = {
  entityParams: object,
  updateAgencyFn:(agencyParams: AgencyModel)=> void,
}

const updateWatermarkSetting = (params: UpdateWatermarkSettingParams) => {
  const {
    entityParams, updateAgencyFn,
  } = params

  const updateWatermarkSettingPayload = {
    callbacks: {
      action: updateAgencyFn,
    },
    entityParams,
    toastText: 'Watermark Setting Updated',
  }

  // @ts-expect-error TS2345
  return handleCallbackAction(updateWatermarkSettingPayload)
}

interface WatermarkSettingTogglesProps {
  agency: AgencyModel,
  brandWatermarkImages: string[],
  callbacks:{
    updateAgency: (agencyParams: AgencyModel) => void,
  },
  hasBrandImageSettings: boolean,
}

const WatermarkSettingToggles = (props: WatermarkSettingTogglesProps) => {
  const {
    agency,
    brandWatermarkImages,
    callbacks: {
      updateAgency: updateAgencyFn,
    },
    hasBrandImageSettings,
  } = props

  const agencyWatermarkImages = agency?.options?.watermark_images || []
  const agencySkipWatermarkPlans = agency?.options?.skip_watermark_plans || false

  const isWaterMarkOnExport = agencyWatermarkImages?.includes('export')
  const isWatermarkOnSoldOrLeased = agencyWatermarkImages?.includes('sold_or_leased')
  const isWaterMarkOnDownload = agencyWatermarkImages?.includes('download')

  const brandIsWatermarkOnExport = brandWatermarkImages?.includes('export')
  const brandIsWatermarkOnSoldOrLeased = brandWatermarkImages?.includes('sold_or_leased')

  useEffect(() => {
    // reset if export is turned off to ensure default watermark_export_options is 'always'
    if (!isWaterMarkOnExport && isWatermarkOnSoldOrLeased){
      updateWatermarkSetting({
        entityParams: { options: { watermark_images: toggleArray(agencyWatermarkImages, 'sold_or_leased') } },
        updateAgencyFn,
      })
    }
  }, [isWaterMarkOnExport])

  return (
    <>
      <SectionDivider boxProps={{ marginY: 'large' }}>WATERMARK SETTINGS</SectionDivider>

      <Box flexDirection="column" gap="medium">
        <ListItem
          boxProps={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 'medium' }}
          disableHover
        >
          <Text fontSize="small">Watermark on Export</Text>
          <ToggleButton
            boxProps={{ width: '80px', justifyContent: 'center' }}
            // @ts-expect-error TS2322
            disabled={hasBrandImageSettings}
            onClick={() => updateWatermarkSetting({
              entityParams: { options: { watermark_images: toggleArray(agencyWatermarkImages, 'export') } },
              updateAgencyFn,
            })}
            toggleOn={hasBrandImageSettings ? brandIsWatermarkOnExport : isWaterMarkOnExport}
            toggleOffText="No"
            toggleOnText="Yes"
          />
        </ListItem>

        {isWaterMarkOnExport && (
          <Box flexDirection="column" gap="medium" marginBottom="medium">
            <Box gap="medium" paddingY="small" alignItems="center">
              <input
                checked={hasBrandImageSettings ? !brandIsWatermarkOnSoldOrLeased : !isWatermarkOnSoldOrLeased}
                disabled={hasBrandImageSettings}
                id="always"
                name="watermark_export_options"
                onChange={() => updateWatermarkSetting({
                  entityParams: { options: { watermark_images: toggleArray(agencyWatermarkImages, 'sold_or_leased') } },
                  updateAgencyFn,
                })}
                style={{ alignSelf: 'center', margin: '0', accentColor: 'green' }}
                type="radio"
                value="always"
              />

              <Text
                as="label"
                color="bodyFontLightColor"
                fontSize="small"
                labelFor="always"
                marginLeft="medium"
              >
                Always
              </Text>
            </Box>
            <Box gap="medium" paddingY="small" alignItems="center" marginTop="medium">
              <input
                checked={hasBrandImageSettings ? brandIsWatermarkOnSoldOrLeased : isWatermarkOnSoldOrLeased}
                disabled={hasBrandImageSettings}
                id="sold_or_leased"
                name="watermark_export_options"
                onChange={() => updateWatermarkSetting({
                  entityParams: { options: { watermark_images: toggleArray(agencyWatermarkImages, 'sold_or_leased') } },
                  updateAgencyFn,
                })}
                style={{ alignSelf: 'center', margin: '0', accentColor: 'green' }}
                type="radio"
                value="sold_or_leased"
              />

              <Text
                as="label"
                color="bodyFontLightColor"
                fontSize="small"
                labelFor="sold_or_leased"
                marginLeft="medium"
              >
                Only when campaign is marked as sold/leased
              </Text>
            </Box>
          </Box>
        )}
      </Box>

      <ListItem
        boxProps={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 'medium' }}
        disableHover
      >
        <Text fontSize="small">Watermark on Download</Text>
        <ToggleButton
          boxProps={{ width: '80px', justifyContent: 'center' }}
          onClick={() => updateWatermarkSetting({
            entityParams: { options: { watermark_images: toggleArray(agencyWatermarkImages, 'download') } },
            updateAgencyFn,
          })}
          toggleOn={isWaterMarkOnDownload}
          toggleOffText="No"
          toggleOnText="Yes"
        />
      </ListItem>

      <ListItem
        boxProps={{ alignItems: 'center', justifyContent: 'space-between' }}
        disableHover
      >
        <Text fontSize="small">Watermark Plans On Download</Text>
        <ToggleButton
          boxProps={{ width: '80px', justifyContent: 'center' }}
          onClick={() => updateWatermarkSetting({
            entityParams: { options: { skip_watermark_plans: !agencySkipWatermarkPlans } },
            updateAgencyFn,
          })}
          toggleOn={!agencySkipWatermarkPlans}
          toggleOffText="No"
          toggleOnText="Yes"
        />
      </ListItem>
    </>
  )
}

export default WatermarkSettingToggles
